import React from 'react';
import { List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { CheckIcon as Check, CloseIcon as Close } from "../../icons";
import { PasswordValidation } from '../hooks/usePasswordValidation';

interface PasswordRequirementsListProps {
  validation: PasswordValidation;
  labels: {
    minLength?: string;
    uppercase?: string;
    lowercase?: string;
    number?: string;
    special?: string;
    notCommon?: string;
    match?: string;
  };
  showMatchRequirement?: boolean;
}

interface RequirementItemProps {
  met: boolean;
  text: string;
}

const RequirementItem = ({ met, text }: RequirementItemProps) => (
  <ListItem dense sx={{ py: 0 }}>
    <ListItemIcon sx={{ minWidth: 32 }}>
      {met ? <Check color="success" fontSize="small" /> : <Close color="error" fontSize="small" />}
    </ListItemIcon>
    <ListItemText
      primary={text}
      primaryTypographyProps={{
        fontSize: '0.8rem',
        color: met ? 'success.main' : 'text.secondary'
      }}
    />
  </ListItem>
);

export const PasswordRequirementsList: React.FC<PasswordRequirementsListProps> = ({
  validation,
  labels,
  showMatchRequirement = true
}) => {
  return (
    <List dense sx={{ mt: 0, mb: 0 }}>
      <RequirementItem
        met={validation.hasMinLength}
        text={labels.minLength || 'At least 8 characters'}
      />
      <RequirementItem
        met={validation.hasUppercase}
        text={labels.uppercase || 'At least one uppercase letter'}
      />
      <RequirementItem
        met={validation.hasLowercase}
        text={labels.lowercase || 'At least one lowercase letter'}
      />
      <RequirementItem
        met={validation.hasNumber}
        text={labels.number || 'At least one number'}
      />
      <RequirementItem
        met={validation.hasSpecialChar}
        text={labels.special || 'At least one special character (!@#$%...)'}
      />
      <RequirementItem
        met={validation.isNotCommon}
        text={labels.notCommon || 'Not a common password'}
      />
      {showMatchRequirement && (
        <RequirementItem
          met={validation.passwordsMatch}
          text={labels.match || 'Passwords match'}
        />
      )}
    </List>
  );
};
